import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { StatusBadge } from '../ui/status-badge';
import { OrderStatus } from '../../lib/types';

interface UpdateStatusFormProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (status: OrderStatus, notes?: string) => void;
  currentStatus: OrderStatus;
}

const statusOptions: { value: OrderStatus; label: string; description: string }[] = [
  { 
    value: 'pending', 
    label: 'Pending',
    description: 'Order is awaiting processing'
  },
  { 
    value: 'confirmed', 
    label: 'Confirmed',
    description: 'Order has been confirmed'
  },
  { 
    value: 'processing', 
    label: 'Processing',
    description: 'Order is being processed'
  },
  { 
    value: 'ready_for_pickup', 
    label: 'Ready for Pickup',
    description: 'Order is ready for pickup by transporter'
  },
  { 
    value: 'picked_up', 
    label: 'Picked Up',
    description: 'Order has been picked up by transporter'
  },
  { 
    value: 'in_transit', 
    label: 'In Transit',
    description: 'Order is in transit to customer'
  },
  { 
    value: 'issue_reported', 
    label: 'Issue Reported',
    description: 'Issue has been reported with this order'
  },
  { 
    value: 'delivered', 
    label: 'Delivered',
    description: 'Order has been delivered'
  },
  { 
    value: 'completed', 
    label: 'Completed',
    description: 'Order is completed'
  },
  { 
    value: 'cancelled', 
    label: 'Cancelled',
    description: 'Order has been cancelled'
  },
  { 
    value: 'rejected', 
    label: 'Rejected',
    description: 'Order has been rejected'
  },
  { 
    value: 'refunded', 
    label: 'Refunded',
    description: 'Order has been refunded'
  }
];

// Status transition rules: which states you can move to from each state
const allowedTransitions: Record<OrderStatus, OrderStatus[]> = {
  'pending': ['confirmed', 'cancelled', 'rejected'],
  'confirmed': ['processing', 'cancelled', 'rejected'],
  'processing': ['ready_for_pickup', 'cancelled', 'rejected'],
  'ready_for_pickup': ['picked_up', 'cancelled', 'rejected', 'issue_reported'],
  'picked_up': ['in_transit', 'cancelled', 'rejected', 'issue_reported'],
  'in_transit': ['delivered', 'cancelled', 'rejected', 'issue_reported'],
  'issue_reported': ['in_transit', 'cancelled', 'rejected', 'delivered', 'ready_for_pickup'],
  'delivered': ['completed', 'cancelled', 'rejected', 'refunded', 'issue_reported'],
  'completed': ['refunded', 'issue_reported'],
  'cancelled': [],
  'rejected': [],
  'refunded': []
};

const getStatusBadgeColor = (status: OrderStatus) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'confirmed':
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'ready_for_pickup':
    case 'in_transit':
      return 'bg-purple-100 text-purple-800';
    case 'delivered':
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
    case 'rejected':
    case 'refunded':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const UpdateStatusForm: React.FC<UpdateStatusFormProps> = ({
  isOpen,
  onClose,
  onUpdate,
  currentStatus
}) => {
  const [status, setStatus] = useState<OrderStatus>(currentStatus);
  const [notes, setNotes] = useState('');
  
  // Get the available status options based on current status and transition rules
  const availableStatuses = [
    currentStatus, // Always include current status
    ...(allowedTransitions[currentStatus] || []) // Use empty array as fallback if status not found
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (status) {
      onUpdate(status, notes);
    }
  };

  const formatStatus = (status: OrderStatus) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Order Status</DialogTitle>
          <DialogDescription>
            Select a new status for this order
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <div className="grid grid-cols-1 gap-2">
              {statusOptions
                .filter(option => availableStatuses.includes(option.value))
                .map(option => (
                  <label 
                    key={option.value} 
                    className={`
                      p-3 flex items-start space-x-3 border rounded-md cursor-pointer
                      ${status === option.value ? 'border-primary ring-1 ring-primary' : ''}
                      ${option.value === currentStatus ? 'bg-gray-50' : ''}
                    `}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={option.value}
                      checked={status === option.value}
                      onChange={() => setStatus(option.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <StatusBadge status={option.value} label={formatStatus(option.value)} />
                        {option.value === currentStatus && (
                          <span className="ml-2 text-xs text-gray-500">
                            (Current)
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    </div>
                  </label>
                ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add notes about this status update..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={status === currentStatus}
            >
              Update Status
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateStatusForm; 